import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import t from 'react-translate';
import type { ClosingReason } from 'lecture_pages/components/ai-role-play/ai-conversation-modal/stream-ai-response';
import { LectureContentQualityStatus } from 'redux/schemas/models/ai-role-play';
import { addAlertMessage } from './alert-messages';

export type AiRolePlaySubmissionCompletedPayload = {
  aiRolePlayId: number;
  aiRolePlaySubmissionId: number;
  pointsReceived: number;
  finishedAt: string;
};

export const setAiRolePlaySubmissionCompleted = createAction<AiRolePlaySubmissionCompletedPayload>(
  'SET_AI_ROLE_PLAY_SUBMISSION_COMPLETED',
);

export type SetClosingReasonPayload = {
  aiRolePlaySubmissionId: number;
  closingReason: ClosingReason;
  aiRolePlayId: number;
};

export const setAiRolePlaySubmissionClosingReason = createAction<SetClosingReasonPayload>(
  'SET_AI_ROLE_PLAY_SUBMISSION_CLOSING_REASON',
);

export type StartSessionPayload = {
  aiApiEndpoint?: string;
  jwtToken?: string;
  submissionId?: number;
  expiresAt?: string;
  languageLocale?: string;
};

type TranscriptAudioRecordingPayload = {
  jwtToken: string;
  aiApiEndpoint: string;
  audioBase64: string;
  languageLocale?: string | null;
};

export const transcriptionLanguageFromLocale = (languageLocale?: string | null) => {
  const language = languageLocale?.trim().split(/[_-]/)[0]?.toLowerCase();

  return language || 'en';
};

export const createAiCharacterObjection = createAsyncThunk(
  'CREATE_AI_CHARACTER_OBJECTION',
  async (payload: { catalogId: string; aiRolePlayId: number; text: string },
    { dispatch, rejectWithValue }) => {
    const { catalogId, aiRolePlayId, text } = payload;
    try {
      const response = await axios.post(
        `/${catalogId}/ai_role_plays/${aiRolePlayId}/ai_character_objections`,
        { ai_character_objection: { text } },
      );

      return response.data.result;
    } catch (error) {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }),
      );

      return rejectWithValue(error);
    }
  },
);

export const deleteAiCharacterObjection = createAsyncThunk(
  'DELETE_AI_CHARACTER_OBJECTION',
  async (payload: { catalogId: string; aiRolePlayId: number; aiCharacterObjectionId: number },
    { dispatch, rejectWithValue }) => {
    const { catalogId, aiRolePlayId, aiCharacterObjectionId } = payload;
    try {
      await axios.delete(`/${catalogId}/ai_role_plays/${aiRolePlayId}/ai_character_objections/${aiCharacterObjectionId}`);

      return { aiCharacterObjectionId, aiRolePlayId };
    } catch (error) {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }),
      );

      return rejectWithValue(error);
    }
  },
);

export const createAiLearnerGoal = createAsyncThunk(
  'CREATE_AI_LEARNER_GOAL',
  async (payload: { catalogId: string; aiRolePlayId: number; text: string },
    { dispatch, rejectWithValue }) => {
    const { catalogId, aiRolePlayId, text } = payload;
    try {
      const response = await axios.post(
        `/${catalogId}/ai_role_plays/${aiRolePlayId}/ai_learner_goals`,
        { ai_learner_goal: { text } },
      );

      return response.data.result;
    } catch (error) {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }),
      );

      return rejectWithValue(error);
    }
  },
);

export const deleteAiLearnerGoal = createAsyncThunk(
  'DELETE_AI_LEARNER_GOAL',
  async (payload: { catalogId: string; aiRolePlayId: number; aiLearnerGoalId: number },
    { dispatch, rejectWithValue }) => {
    const { catalogId, aiRolePlayId, aiLearnerGoalId } = payload;
    try {
      await axios.delete(`/${catalogId}/ai_role_plays/${aiRolePlayId}/ai_learner_goals/${aiLearnerGoalId}`);

      return { aiLearnerGoalId, aiRolePlayId };
    } catch (error) {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.ERROR_SOMETHING_WRONG(),
        }),
      );

      return rejectWithValue(error);
    }
  },
);
type GenerateAiRolePlayScenarioPayload = {
  catalogId: string;
  contextDescription: string;
  lecturePageId: number;
  lectureComponentId?: number;
  title?: string;
};

export const initiateAIRolePlayScenarioGeneration = createAsyncThunk(
  'INITIATE_AI_ROLE_PLAY_SCENARIO_GENERATION',
  async (payload: GenerateAiRolePlayScenarioPayload, { dispatch, rejectWithValue }) => {
    const {
      catalogId,
      contextDescription,
      lecturePageId,
      lectureComponentId = null,
      title = null,
    } = payload;

    try {
      const response = await axios.post(
        `/${catalogId}/ai_role_plays/generations`,
        { description: contextDescription, lecturePageId, lectureComponentId, title },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const startRolePlaySession = createAsyncThunk<StartSessionPayload, { catalogId: string; aiRolePlayId: number }>(
  'START_ROLE_PLAY_SESSION',
  async (payload: { catalogId: string; aiRolePlayId: number }, { rejectWithValue }) => {
    const { catalogId, aiRolePlayId } = payload;
    try {
      const response = await axios.post<StartSessionPayload>(
        `/${catalogId}/ai_role_plays/${aiRolePlayId}/submissions`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getRefreshToken = createAsyncThunk(
  'GET_REFRESH_TOKEN',
  async (payload: { submissionId: number, catalogId: string }, { rejectWithValue }) => {
    const { submissionId, catalogId } = payload;
    try {
      const response = await axios.post(
        `/${catalogId}/ai_role_play_submissions/${submissionId}/refresh_token`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAiRolePlaySubmission = createAsyncThunk(
  'GET_AI_ROLE_PLAY_SUBMISSION',
  async (payload: { submissionId: number; catalogId: string }, { rejectWithValue }) => {
    const { submissionId, catalogId } = payload;
    try {
      const response = await axios.get(
        `/${catalogId}/ai_role_play_submissions/${submissionId}`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAiRolePlayConversation = createAsyncThunk(
  'GET_AI_ROLE_PLAY_CONVERSATION',
  async (payload: { submissionId: number; catalogId: string }, { rejectWithValue }) => {
    const { submissionId, catalogId } = payload;
    try {
      const response = await axios.get(
        `/${catalogId}/ai_role_play_submissions/${submissionId}/messages?format=json`,
      );

      return response.data.result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const SUBMISSIONS_LIST_FILTERS = ['all', 'excellent', 'good_job', 'needs_work'] as const;
export type SubmissionsListFilter = typeof SUBMISSIONS_LIST_FILTERS[number];

export type GetAiRolePlaySubmissionsListPayload = {
  catalogId: string;
  aiRolePlayId: number;
  page?: number;
  pageSize?: number;
  filterBy?: SubmissionsListFilter;
  search?: string;
  userId?: number;
};

export const getAiRolePlaySubmissionsList = createAsyncThunk(
  'GET_AI_ROLE_PLAY_SUBMISSIONS_LIST',
  async (payload: GetAiRolePlaySubmissionsListPayload, { rejectWithValue, signal }) => {
    const {
      catalogId,
      aiRolePlayId,
      page = 1,
      pageSize = 10,
      filterBy = 'all',
      search,
      userId,
    } = payload;
    try {
      const response = await axios.get(
        `/${catalogId}/ai_role_plays/${aiRolePlayId}/submissions.json`,
        {
          params: {
            page,
            page_size: pageSize,
            filter_by: filterBy,
            search,
            user_id: userId,
          },
          signal,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchAiRolePlay = createAsyncThunk(
  'FETCH_AI_ROLE_PLAY',
  async (payload: { aiRolePlayId: number }, { rejectWithValue }) => {
    const { aiRolePlayId } = payload;
    try {
      const response = await axios.get(`/ai_role_plays/${aiRolePlayId}.json`);
      return response.data.result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCourseRolePlays = createAsyncThunk(
  'GET_AI_ROLE_PLAYS',
  async ({ catalogId, completedSubmissionOnly, nameList }: { catalogId: string; completedSubmissionOnly?: boolean; nameList?: boolean }) => axios.get(`/${catalogId}/ai_role_plays`, {
    params: { format: 'json', completed_submission_only: completedSubmissionOnly, name_list: nameList },
  }).then(response => {
    const { result } = response.data;
    return (Array.isArray(result) ? result : Object.values(result || {})) as { id: number; title: string }[];
  }),
);

export const getAiRolePlayAvailableFaces = createAsyncThunk(
  'GET_AI_ROLE_PLAY_AVAILABLE_FACES',
  async (payload: { catalogId: string }, { rejectWithValue }) => {
    const { catalogId } = payload;
    try {
      const response = await axios.get(`/${catalogId}/ai_role_plays/available_face_presets.json`);
      return response.data.result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Using fetch instead of axios for external AI service API to avoid CORS preflight issues
// caused by axios interceptors adding custom headers (e.g., X-Requested-With)
export const transcriptAudioRecording = createAsyncThunk(
  'TRANSCRIPT_AUDIO_RECORDING',
  async (payload: TranscriptAudioRecordingPayload, { rejectWithValue }) => {
    const {
      jwtToken,
      aiApiEndpoint,
      audioBase64,
      languageLocale,
    } = payload;
    try {
      const response = await fetch(`${aiApiEndpoint}/transcribe/audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          audio_base64: audioBase64,
          language: transcriptionLanguageFromLocale(languageLocale),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.transcript;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const validateLectureContentForAiRolePlay = createAsyncThunk(
  'VALIDATE_LECTURE_CONTENT_FOR_AI_ROLE_PLAY',
  async (payload: { catalogId: string; lecturePageId: number }, { rejectWithValue }) => {
    const { catalogId, lecturePageId } = payload;
    try {
      const response = await axios.post<{ contentQuality: LectureContentQualityStatus }>(
        `/${catalogId}/ai_role_plays/generations/validate`,
        { lecturePageId },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
